import { parseWorkspaceHost } from "@slate/shared";

/**
 * The public booking page's calls to apps/services (modules/booking). No
 * Supabase session is involved: the page works for anyone with the link.
 */

export const BOOKING_PATH_PREFIX = "/meeting/";

export interface PublicBookingPage {
  hostName: string;
  title: string;
  durationMinutes: number;
  timeZone: string;
  maxDaysAhead: number;
  turnstileSiteKey?: string;
}

export interface BookedMeeting {
  id: string;
  start: string;
  end: string;
  title: string;
  hostName: string;
}

export class BookingApiError extends Error {
  constructor(
    public status: number,
    public reason: string,
  ) {
    super(reason);
  }
}

/** This page's workspace: the subdomain, or VITE_WORKSPACE_SLUG on localhost. */
export const bookingWorkspaceSlug = (): string | undefined => {
  const host = parseWorkspaceHost(window.location.hostname);
  if (host.kind === "tenant") return host.slug;
  return import.meta.env.VITE_WORKSPACE_SLUG || undefined;
};

/** The shareable link for a handle, on this workspace's own address. */
export const bookingLinkFor = (handle: string): string =>
  `${window.location.origin}${BOOKING_PATH_PREFIX}${handle}`;

const servicesUrl = () =>
  (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const base = servicesUrl();
  if (!base) throw new BookingApiError(0, "not_configured");
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, init);
  } catch {
    throw new BookingApiError(0, "network");
  }
  const body = (await res.json().catch(() => ({}))) as { reason?: string } & T;
  if (!res.ok) throw new BookingApiError(res.status, body.reason ?? "error");
  return body;
}

const pathFor = (slug: string, handle: string) =>
  `/m/${encodeURIComponent(slug)}/${encodeURIComponent(handle)}`;

export const fetchBookingPage = (slug: string, handle: string) =>
  call<{ page: PublicBookingPage }>(pathFor(slug, handle)).then((b) => b.page);

export const fetchSlots = (
  slug: string,
  handle: string,
  from: Date,
  to: Date,
) =>
  call<{ slots: string[] }>(
    `${pathFor(slug, handle)}/slots?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
  ).then((b) => b.slots);

export interface BookRequest {
  start: string;
  name: string;
  email: string;
  notes?: string;
  timeZone?: string;
  /** Honeypot. */
  website?: string;
  captchaToken?: string;
}

export const bookSlot = (
  slug: string,
  handle: string,
  body: BookRequest,
): Promise<BookedMeeting> =>
  call<{ meeting: BookedMeeting }>(`${pathFor(slug, handle)}/book`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).then((b) => b.meeting);
